import type {
  AdminOverview,
  AdminRule,
  AuditEntry,
  BetaReport,
  BetaReportPatch,
  BetaSettings,
  BetaSettingsResponse,
  BetaSignup,
  BetaWelcomeRequest,
  BetaWelcomeResult,
  GrantRequest,
  TeamGrantRequest,
  GrantResult,
  ProGrant,
  PlatformSavingsMonth,
  WorkspaceSavings,
  WorkspacePlan,
  WorkspaceBilling,
  BillingInterval,
  CheckoutSession,
  BrandSettings,
  CommentItem,
  GuideStats,
  NotificationItem,
  GuideSummary,
  InviteCreated,
  PatchWorkspaceRequest,
  InvitePreview,
  InviteSummary,
  Member,
  MeResponse,
  MeSummary,
  PatchGuideRequest,
  CopyGuideRequest,
  PutGuideRequest,
  PutGuideResponse,
  PublicGuide,
  RemoteGuide,
  Role,
  WorkspaceStats,
  WorkspaceSummary,
} from '@nolens/domain';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, apiUpload } from './api';
import { rememberImageAccess } from './imageAccess';

/** Guarda as assinaturas das miniaturas que vieram com a lista. */
const withListAccess = (list: GuideSummary[]) => {
  for (const g of list) rememberImageAccess(g.id, g.imageAccess);
  return list;
};

export const useMe = (enabled = true) =>
  useQuery({ queryKey: ['me'], queryFn: () => api<MeResponse>('/v1/me'), enabled });

export const useGuides = (workspaceId: string | undefined) =>
  useQuery({
    queryKey: ['guides', workspaceId],
    queryFn: () => api<GuideSummary[]>(`/v1/guides?workspace=${workspaceId}`).then(withListAccess),
    enabled: !!workspaceId,
  });

/** Guias de todos os espaços da pessoa, mais recentes primeiro (Início e pesquisa). */
export const useAllGuides = (opts: { mine?: boolean; limit?: number; enabled?: boolean } = {}) => {
  const params = new URLSearchParams();
  if (opts.mine) params.set('mine', '1');
  if (opts.limit) params.set('limit', String(opts.limit));
  const qs = params.toString();
  return useQuery({
    queryKey: ['guides', 'all', qs],
    queryFn: () => api<GuideSummary[]>(`/v1/guides${qs ? `?${qs}` : ''}`).then(withListAccess),
    enabled: opts.enabled ?? true,
  });
};

/** Resumo da pessoa no Início: guias gravados, poupança e leituras. */
export const useMeSummary = () =>
  useQuery({ queryKey: ['summary'], queryFn: () => api<MeSummary>('/v1/me/summary') });

/** Leituras de um espaço (todos os membros). */
export const useWorkspaceStats = (ws: string | undefined) =>
  useQuery({
    queryKey: ['wsstats', ws],
    queryFn: () => api<WorkspaceStats>(`/v1/workspaces/${ws}/stats`),
    enabled: !!ws,
  });

export const useGuide = (id: string | undefined) =>
  useQuery({
    queryKey: ['guide', id],
    queryFn: () =>
      api<RemoteGuide>(`/v1/guides/${id}`).then((g) => {
        rememberImageAccess(g.id, g.imageAccess);
        return g;
      }),
    enabled: !!id,
  });

export const usePublicGuide = (token: string | undefined) =>
  useQuery({
    queryKey: ['public', token],
    queryFn: () => api<PublicGuide>(`/public/guides/${token}`, { anonymous: true }),
    enabled: !!token,
  });

export const useMembers = (ws: string | undefined) =>
  useQuery({
    queryKey: ['members', ws],
    queryFn: () => api<Member[]>(`/v1/workspaces/${ws}/members`),
    enabled: !!ws,
  });

export const useInvites = (ws: string | undefined, enabled: boolean) =>
  useQuery({
    queryKey: ['invites', ws],
    queryFn: () => api<InviteSummary[]>(`/v1/workspaces/${ws}/invites`),
    enabled: !!ws && enabled,
  });

export const useInvitePreview = (token: string | undefined) =>
  useQuery({
    queryKey: ['invite', token],
    queryFn: () => api<InvitePreview>(`/v1/invites/${token}`),
    enabled: !!token,
  });

export function usePatchGuide(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PatchGuideRequest) =>
      api<RemoteGuide>(`/v1/guides/${id}`, { method: 'PATCH', body }),
    onSuccess: (g) => {
      qc.setQueryData(['guide', id], g);
      void qc.invalidateQueries({ queryKey: ['guides'] });
      void qc.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

/** Guia novo a partir de um ficheiro importado (domain/import.ts), sem capturas. */
export function useImportGuide() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: PutGuideRequest }) =>
      api<PutGuideResponse>(`/v1/guides/${id}`, { method: 'PUT', body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['guides'] });
      void qc.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

/** Guardar como cópia: um guia novo no mesmo espaço, com as capturas copiadas. */
export function useCopyGuide(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CopyGuideRequest) =>
      api<RemoteGuide>(`/v1/guides/${id}/copy`, { method: 'POST', body }),
    onSuccess: (g) => {
      qc.setQueryData(['guide', g.id], g);
      void qc.invalidateQueries({ queryKey: ['guides'] });
      void qc.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

export function useDeleteGuide() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/v1/guides/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['guides'] });
      void qc.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

export function useCreateTeam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) =>
      api<WorkspaceSummary>('/v1/workspaces', { method: 'POST', body: { name } }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['me'] }),
  });
}

export function useWorkspaceActions(ws: string) {
  const qc = useQueryClient();
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['members', ws] });
    void qc.invalidateQueries({ queryKey: ['invites', ws] });
    void qc.invalidateQueries({ queryKey: ['me'] });
  };
  return {
    rename: useMutation({
      mutationFn: (name: string) =>
        api(`/v1/workspaces/${ws}`, { method: 'PATCH', body: { name } }),
      onSuccess: refresh,
    }),
    /** Políticas das gravações (só o dono). */
    policies: useMutation({
      mutationFn: (p: Pick<PatchWorkspaceRequest, 'reviewRequired' | 'keepTyped'>) =>
        api(`/v1/workspaces/${ws}`, { method: 'PATCH', body: p }),
      onSuccess: refresh,
    }),
    remove: useMutation({
      mutationFn: () => api(`/v1/workspaces/${ws}`, { method: 'DELETE' }),
      onSuccess: refresh,
    }),
    setRole: useMutation({
      mutationFn: ({ userId, role }: { userId: string; role: Role }) =>
        api(`/v1/workspaces/${ws}/members/${userId}`, { method: 'PATCH', body: { role } }),
      onSuccess: refresh,
    }),
    removeMember: useMutation({
      mutationFn: (userId: string) =>
        api(`/v1/workspaces/${ws}/members/${userId}`, { method: 'DELETE' }),
      onSuccess: refresh,
    }),
    invite: useMutation({
      mutationFn: ({ role, email }: { role: 'editor' | 'viewer'; email?: string }) =>
        api<InviteCreated>(`/v1/workspaces/${ws}/invites`, {
          method: 'POST',
          body: email ? { role, email } : { role },
        }),
      onSuccess: refresh,
    }),
    revoke: useMutation({
      mutationFn: (id: string) => api(`/v1/workspaces/${ws}/invites/${id}`, { method: 'DELETE' }),
      onSuccess: refresh,
    }),
  };
}

// ── Fase 3 ────────────────────────────────────────────────────────────────

export const useBrand = (ws: string | undefined) =>
  useQuery({
    queryKey: ['brand', ws],
    queryFn: () => api<BrandSettings | null>(`/v1/workspaces/${ws}/brand`),
    enabled: !!ws,
  });

export const useComments = (guideId: string) =>
  useQuery({
    queryKey: ['comments', guideId],
    queryFn: () => api<CommentItem[]>(`/v1/guides/${guideId}/comments`),
  });

export const useStats = (guideId: string) =>
  useQuery({
    queryKey: ['stats', guideId],
    queryFn: () => api<GuideStats>(`/v1/guides/${guideId}/stats`),
  });

export const useNotifications = () =>
  useQuery({
    queryKey: ['notifications'],
    queryFn: () => api<NotificationItem[]>('/v1/notifications'),
    refetchInterval: 60_000,
  });

export function useCommentActions(guideId: string) {
  const qc = useQueryClient();
  const refresh = () => void qc.invalidateQueries({ queryKey: ['comments', guideId] });
  return {
    add: useMutation({
      mutationFn: (b: { body: string; stepId?: string | null }) =>
        api<CommentItem>(`/v1/guides/${guideId}/comments`, { method: 'POST', body: b }),
      onSuccess: () => {
        refresh();
        void qc.invalidateQueries({ queryKey: ['guide', guideId] });
      },
    }),
    resolve: useMutation({
      mutationFn: ({ id, resolved }: { id: string; resolved: boolean }) =>
        api(`/v1/comments/${id}`, { method: 'PATCH', body: { resolved } }),
      onSuccess: refresh,
    }),
    remove: useMutation({
      mutationFn: (id: string) => api(`/v1/comments/${id}`, { method: 'DELETE' }),
      onSuccess: refresh,
    }),
  };
}

export function useSubscription(guideId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (on: boolean) =>
      api(`/v1/guides/${guideId}/subscription`, { method: on ? 'PUT' : 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['guide', guideId] }),
  });
}

export function useBrandActions(ws: string) {
  const qc = useQueryClient();
  const set = (b: BrandSettings | null) => qc.setQueryData(['brand', ws], b);
  return {
    save: useMutation({
      mutationFn: (
        b: Pick<
          BrandSettings,
          'accent' | 'font' | 'footer' | 'companyName' | 'coverColor' | 'highlightColor'
        >,
      ) => api<BrandSettings>(`/v1/workspaces/${ws}/brand`, { method: 'PUT', body: b }),
      onSuccess: set,
    }),
    upload: useMutation({
      mutationFn: ({ what, file }: { what: 'logo' | 'font'; file: Blob }) =>
        apiUpload<BrandSettings>(`/v1/workspaces/${ws}/brand/${what}`, file),
      onSuccess: set,
    }),
    removeFile: useMutation({
      mutationFn: (what: 'logo' | 'font') =>
        api<BrandSettings | null>(`/v1/workspaces/${ws}/brand/${what}`, { method: 'DELETE' }),
      onSuccess: set,
    }),
  };
}

// ── Administração (proprietário; a API recusa a quem não o é) ─────────────

export const useAdminOverview = () =>
  useQuery({
    queryKey: ['admin', 'overview'],
    queryFn: () => api<AdminOverview>('/v1/admin/overview'),
  });
export const useAdminRules = () =>
  useQuery({ queryKey: ['admin', 'rules'], queryFn: () => api<AdminRule[]>('/v1/admin/rules') });
export const useAdminAudit = () =>
  useQuery({
    queryKey: ['admin', 'audit'],
    queryFn: () => api<AuditEntry[]>('/v1/admin/audit?limit=300'),
  });
export const useAdminBetaSignups = () =>
  useQuery({
    queryKey: ['admin', 'beta-signups'],
    queryFn: () => api<BetaSignup[]>('/v1/admin/beta-signups'),
  });
export const useAdminGrants = () =>
  useQuery({ queryKey: ['admin', 'grants'], queryFn: () => api<ProGrant[]>('/v1/admin/grants') });

/** Acessos dados pelo proprietário: teste do Pro, retirá-lo e prolongar o teste das equipas. */
export function useGrantActions() {
  const qc = useQueryClient();
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['admin', 'grants'] });
    void qc.invalidateQueries({ queryKey: ['admin', 'audit'] });
    void qc.invalidateQueries({ queryKey: ['admin', 'beta-signups'] });
  };
  return {
    pro: useMutation({
      mutationFn: (b: GrantRequest) =>
        api<GrantResult>('/v1/admin/grants/pro', { method: 'POST', body: b }),
      onSuccess: refresh,
    }),
    revoke: useMutation({
      mutationFn: (email: string) =>
        api<void>('/v1/admin/grants/pro/revoke', { method: 'POST', body: { email } }),
      onSuccess: refresh,
    }),
    team: useMutation({
      mutationFn: (b: TeamGrantRequest) =>
        api<GrantResult>('/v1/admin/grants/team', { method: 'POST', body: b }),
      onSuccess: refresh,
    }),
  };
}

// ── Programa de testes ───────────────────────────────────────────────────

export const useAdminBetaSettings = () =>
  useQuery({
    queryKey: ['admin', 'beta-settings'],
    queryFn: () => api<BetaSettingsResponse>('/v1/admin/beta/settings'),
  });

/** Guardar o link e o texto; enviar as boas-vindas (um a um, aos pendentes ou um teste). */
export function useBetaActions() {
  const qc = useQueryClient();
  return {
    save: useMutation({
      mutationFn: (b: BetaSettings) =>
        api<void>('/v1/admin/beta/settings', { method: 'PUT', body: b }),
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: ['admin', 'beta-settings'] });
        void qc.invalidateQueries({ queryKey: ['admin', 'audit'] });
      },
    }),
    welcome: useMutation({
      mutationFn: (b: BetaWelcomeRequest) =>
        api<BetaWelcomeResult>('/v1/admin/beta/welcome', { method: 'POST', body: b }),
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: ['admin', 'beta-signups'] });
        void qc.invalidateQueries({ queryKey: ['admin', 'audit'] });
      },
    }),
  };
}

export const useAdminBetaReports = () =>
  useQuery({
    queryKey: ['admin', 'beta-reports'],
    queryFn: () => api<BetaReport[]>('/v1/admin/beta/reports'),
  });

export function useBetaReportActions() {
  const qc = useQueryClient();
  const refresh = () => void qc.invalidateQueries({ queryKey: ['admin', 'beta-reports'] });
  return {
    update: useMutation({
      mutationFn: ({ id, ...b }: BetaReportPatch & { id: string }) =>
        api<void>(`/v1/admin/beta/reports/${id}`, { method: 'PATCH', body: b }),
      onSuccess: refresh,
    }),
    remove: useMutation({
      mutationFn: (id: string) => api<void>(`/v1/admin/beta/reports/${id}`, { method: 'DELETE' }),
      onSuccess: refresh,
    }),
  };
}

export const useAdminSavings = () =>
  useQuery({
    queryKey: ['admin', 'savings'],
    queryFn: () => api<PlatformSavingsMonth[]>('/v1/admin/savings'),
  });

/** Poupança de um espaço (só o dono do espaço). */
export const useWorkspaceSavings = (ws: string | undefined) =>
  useQuery({
    queryKey: ['savings', ws],
    queryFn: () => api<WorkspaceSavings>(`/v1/workspaces/${ws}/savings`),
    enabled: !!ws,
  });

export function useRuleActions() {
  const qc = useQueryClient();
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['admin', 'rules'] });
    void qc.invalidateQueries({ queryKey: ['admin', 'audit'] });
    void qc.invalidateQueries({ queryKey: ['admin', 'overview'] });
  };
  return {
    create: useMutation({
      mutationFn: (b: Pick<AdminRule, 'pattern' | 'mode' | 'note'>) =>
        api<AdminRule>('/v1/admin/rules', { method: 'POST', body: b }),
      onSuccess: refresh,
    }),
    update: useMutation({
      mutationFn: ({
        id,
        ...b
      }: Partial<Pick<AdminRule, 'pattern' | 'mode' | 'note'>> & { id: string }) =>
        api<AdminRule>(`/v1/admin/rules/${id}`, { method: 'PATCH', body: b }),
      onSuccess: refresh,
    }),
    remove: useMutation({
      mutationFn: (id: string) => api<void>(`/v1/admin/rules/${id}`, { method: 'DELETE' }),
      onSuccess: refresh,
    }),
  };
}

/** Plano e uso de um espaço (membros). */
export const useWorkspacePlan = (ws: string | undefined) =>
  useQuery({
    queryKey: ['plan', ws],
    queryFn: () => api<WorkspacePlan>(`/v1/workspaces/${ws}/plan`),
    enabled: !!ws,
  });

export const useWorkspaceBilling = (ws: string | undefined) =>
  useQuery({
    queryKey: ['billing', ws],
    queryFn: () => api<WorkspaceBilling>(`/v1/workspaces/${ws}/billing`),
    enabled: !!ws,
  });

/** Checkout e portal do cliente (Paddle). */
export function useBillingActions(ws: string) {
  return {
    checkout: useMutation({
      mutationFn: (interval: BillingInterval) =>
        api<CheckoutSession>(`/v1/workspaces/${ws}/billing/checkout`, {
          method: 'POST',
          body: { interval },
        }),
    }),
    portal: useMutation({
      mutationFn: () =>
        api<{ url: string }>(`/v1/workspaces/${ws}/billing/portal`, { method: 'POST' }),
    }),
  };
}
